import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';

export interface MockResponse {
  endpoint: string;
  method: string;
  response: unknown;
  statusCode?: number;
  delay?: number;
}

@Injectable({
  providedIn: 'root'
})
export class DevToolsService {
  private mockResponses = new Map<string, MockResponse>();
  private isEnabled = false;

  constructor() {
    console.log('DevToolsService initialized',environment.enableDevTools);
    this.isEnabled = environment.enableDevTools;
    this.loadMockResponses();
    this.seedDefaultMocks();
  }


  isDevToolsEnabled(): boolean {
    return this.isEnabled;
  }


  getMockResponse(endpoint: string, method: string = 'GET'): MockResponse | null {
    if (!this.isEnabled) {
      return null;
    }

    const key = this.createMockKey(endpoint, method);
    return this.mockResponses.get(key) || null;
  }


  setMockResponse(mock: MockResponse): void {
    if (!this.isEnabled) {
      return;
    }

    const key = this.createMockKey(mock.endpoint, mock.method);
    this.mockResponses.set(key, {
      ...mock,
      statusCode: mock.statusCode || 200,
      delay: mock.delay || 0
    });
    this.saveMockResponses();
  }


  removeMockResponse(endpoint: string, method: string = 'GET'): boolean {
    if (!this.isEnabled) {
      return false;
    }

    const key = this.createMockKey(endpoint, method);
    const removed = this.mockResponses.delete(key);
    if (removed) {
      this.saveMockResponses();
    }
    return removed;
  }


  getAllMockResponses(): MockResponse[] {
    if (!this.isEnabled) {
      return [];
    }

    return Array.from(this.mockResponses.values());
  }

  clearAllMockResponses(): void {
    if (!this.isEnabled) {
      return;
    }

    this.mockResponses.clear();
    this.saveMockResponses();
  }

  exportMockResponses(): string {
    if (!this.isEnabled) {
      return '';
    }

    const mockData = Array.from(this.mockResponses.entries());
    return JSON.stringify(mockData, null, 2);
  }

  importMockResponses(jsonData: string): boolean {
    if (!this.isEnabled) {
      return false;
    }

    try {
      const mockData = JSON.parse(jsonData);
      this.mockResponses = new Map(mockData);
      this.saveMockResponses();
      return true;
    } catch (error) {
      console.error('Failed to import mock responses:', error);
      return false;
    }
  }

  getMockStatistics(): { total: number; byMethod: Record<string, number> } {
    if (!this.isEnabled) {
      return { total: 0, byMethod: {} };
    }

    const byMethod: Record<string, number> = {};
    this.mockResponses.forEach((mock) => {
      byMethod[mock.method] = (byMethod[mock.method] || 0) + 1;
    });

    return {
      total: this.mockResponses.size,
      byMethod
    };
  }

  private createMockKey(endpoint: string, method: string): string {
    return `${method.toUpperCase()}:${endpoint}`;
  }

  private saveMockResponses(): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      const mockData = Array.from(this.mockResponses.entries());
      localStorage.setItem('dev-tools-mock-responses', JSON.stringify(mockData));
    } catch (error) {
      console.error('Failed to save mock responses:', error);
    }
  }

  private loadMockResponses(): void {
    if (!this.isEnabled) {
      return;
    }

    try {
      const mockData = localStorage.getItem('dev-tools-mock-responses');
      if (mockData) {
        const parsedData = JSON.parse(mockData);
        this.mockResponses = new Map(parsedData);
      }
    } catch (error) {
      console.error('Failed to load mock responses:', error);
      this.mockResponses = new Map();
    }
  }

  /**
   * TEMPORARY local aid — set SEED_DEFAULT_MOCKS to false (or delete this) once
   * the dev backend serves coverage requirements again.
   *
   * `POST /v2/coverage_requirements/configuration/community` returns nothing on
   * the current dev data, and the whole liability/contents minimum behaviour
   * hangs off it: Validators.min, the disabled below-minimum presets, the
   * "Minimum coverage is X" hint, and the FE-190 error copy are all unreachable
   * without a response. This seeds one so that path can be exercised locally.
   *
   * Amounts are CENTS, which is what the API speaks — 30000000 is $300,000.
   *
   * Two deliberate limits so this cannot mask anything:
   *   · devtools are off outside dev/default environments, so the interceptor is
   *     inert in stage and prod regardless of this.
   *   · a seed NEVER overwrites a mock already in localStorage, so anything set
   *     by hand or through /dev-tools wins and stays won.
   * The interceptor logs every mocked call, so a seeded response is always
   * visible in the console rather than silently standing in for the real API.
   */
  /*
   * OFF as of GC-315. The seeded liability of 30000000 ($300,000) is returned
   * for ANY start_date, which is a date-independent answer to a date-dependent
   * question — and coverage requirements are effective-dated. On the upload
   * screen that is indistinguishable from a correctly resolved requirement, so a
   * broken date-resolution fix looks like a working one. The interceptor also
   * answers before the request reaches the network, so "no request was made" and
   * "answered locally" look identical in the Network tab.
   *
   * Re-enable only if the dev backend stops serving coverage requirements again,
   * and clear it before verifying anything date-dependent.
   */
  private static readonly SEED_DEFAULT_MOCKS = false;

  private static readonly DEFAULT_MOCKS: MockResponse[] = [
    {
      endpoint: '/v2/coverage_requirements/configuration/community',
      method: 'POST',
      statusCode: 200,
      response: {
        data: [
          // $300,000 — a value the preset segments can express.
          { designation: 'liability', amount: 30000000 },
          // $10,000 contents; divided by 100 downstream for that dollars field.
          { designation: 'contents', amount: 1000000 },
        ],
      },
    },
  ];

  private seedDefaultMocks(): void {
    if (!this.isEnabled || !DevToolsService.SEED_DEFAULT_MOCKS) {
      return;
    }

    let seeded = 0;
    for (const mock of DevToolsService.DEFAULT_MOCKS) {
      const key = this.createMockKey(mock.endpoint, mock.method);
      if (this.mockResponses.has(key)) {
        continue; // an existing mock always wins
      }
      this.mockResponses.set(key, { statusCode: 200, delay: 0, ...mock });
      seeded++;
    }

    if (seeded > 0) {
      console.warn(
        `[Dev Tools] Seeded ${seeded} DEFAULT mock response(s). These are local ` +
        'development stand-ins, not real API data — see seedDefaultMocks() in ' +
        'dev-tools.service.ts to change or disable them.',
      );
      this.saveMockResponses();
    }
  }
}
